import { useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { YouTubePlayer } from "../components/YouTubePlayer.js";
import { getBearer } from "../lib/auth.js";
import { IS_TV } from "../lib/tvMode.js";

/**
 * Standalone player for YouTube videos not in the DB.
 * TV mode: sends to mpv via yt-dlp, sends heartbeats, returns to lobby on stop.
 * Browser mode: YouTube iframe embed.
 */
export function WatchYouTube(): JSX.Element {
  const { videoId = "" } = useParams<{ videoId: string }>();
  const nav = useNavigate();
  const externalUrl = `https://www.youtube.com/watch?v=${encodeURIComponent(videoId)}`;
  const launched = useRef(false);

  useEffect(() => {
    if (!IS_TV || !videoId || launched.current) return;
    launched.current = true;
    const ytUrl = `https://www.youtube.com/watch?v=${encodeURIComponent(videoId)}`;
    const token = getBearer() ?? "";
    fetch("/api/cast/mpv", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
      body: JSON.stringify({ url: ytUrl }),
    }).catch(() => undefined);

    let cancelled = false;
    const beat = (): void => {
      fetch("/api/cast/playing-heartbeat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ item_id: `yt:${videoId}`, kind: "youtube", title: null }),
      }).catch(() => undefined);
    };

    void (async () => {
      // Wait up to 20s for mpv to start
      for (let i = 0; i < 20 && !cancelled; i++) {
        await new Promise(r => setTimeout(r, 1000));
        try {
          const r = await fetch("/api/cast/mpv/status");
          if (r.ok && (await r.json() as { active: boolean }).active) { beat(); break; }
        } catch { /* retry */ }
      }
      // Heartbeat while active; require 4 consecutive inactive before returning to lobby
      let inactive = 0;
      while (!cancelled) {
        await new Promise(r => setTimeout(r, 4000));
        if (cancelled) return;
        try {
          const r = await fetch("/api/cast/mpv/status");
          if (r.ok && (await r.json() as { active: boolean }).active) {
            inactive = 0;
            if (!cancelled) beat();
          } else {
            inactive++;
            if (inactive >= 4) {
              // Ended. If autoplay-next is on, play a related video; else lobby.
              const ap = await fetch("/api/cast/autoplay").then(x => x.json()).then((x: { enabled?: boolean }) => !!x.enabled).catch(() => false);
              if (ap) {
                try {
                  const token = getBearer() ?? "";
                  const rel = await fetch(`/tv/youtube/related?video_id=${encodeURIComponent(videoId)}`, { headers: { Authorization: `Bearer ${token}` } });
                  if (rel.ok) {
                    const j = await rel.json() as { video_id?: string };
                    if (j.video_id) { nav(`/watch/yt/${encodeURIComponent(j.video_id)}?autoplay=1`); return; }
                  }
                } catch { /* fall through to lobby */ }
              }
              nav("/");
              return;
            }
          }
        } catch { /* blip — don't count */ }
      }
    })();

    return () => {
      cancelled = true;
      fetch("/api/cast/playing-heartbeat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ item_id: `yt:${videoId}`, kind: "youtube", title: null, stopped: true }),
      }).catch(() => undefined);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId]);

  // TV mode: show minimal "Now Playing" (mpv has the window)
  if (IS_TV) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-lacquer-deep select-none"
        style={{ fontFamily: "'Cinzel', serif" }}>
        <div className="section-label text-gold tracking-[0.4em] text-sm">NOW PLAYING</div>
        <div className="font-display text-ivory text-2xl mt-4">YouTube</div>
        <div className="flex gap-1.5 mt-4">
          {[0, 1, 2].map(i => (
            <span key={i} className="block w-1.5 h-1.5 rounded-full bg-gold/60 animate-pulse"
              style={{ animationDelay: `${i * 300}ms` }} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1100px] mx-auto px-10 py-8">
      <button onClick={() => nav(-1)} className="pill-ghost mb-4">
        <ArrowLeft size={14} /> Back
      </button>
      <div className="rounded-lg overflow-hidden border border-hairline aspect-video bg-ink">
        <YouTubePlayer videoId={videoId} />
      </div>
      <div className="mt-6 flex items-start gap-6">
        <div className="flex-1 min-w-0">
          <div className="section-label">YouTube — via your API key</div>
          <h1 className="hero-title mt-2">Streaming directly</h1>
          <p className="text-parchment mt-4 max-w-[80ch]">
            This result came from a live YouTube search, not Wallace-TV's catalog —
            so we're not tracking watch events for it.
          </p>
        </div>
        <div className="flex flex-col gap-2 w-44 shrink-0">
          <a href={externalUrl} target="_blank" rel="noreferrer" className="pill-ghost">
            <ExternalLink size={14} /> Open on YouTube
          </a>
        </div>
      </div>
    </div>
  );
}
